// noinspection DuplicatedCode

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PostBacksContext from '@/modules/pb/contexts/PostBacksContext';
import Paper from '@/modules/common/components/Paper';
import Content from '@/modules/common/components/Content';
import Spinner from '@/modules/common/components/Spinner';
import ErrorFetch from '@/modules/common/components/ErrorFetch';
import Pagination from '@/modules/common/components/Pagination';
import ContentBody from '@/modules/common/components/ContentBody';
import ContentHeader from '@/modules/common/components/ContentHeader';
import { withFetch } from '~/hocs/fetch';
import { withRouter } from '~/hocs/router';
import Top from './Top';
import Table from './Table/Table';
import DialogAdd from './DialogAdd';
import DialogEdit from './DialogEdit';
import ContextMenu from './Table/ContextMenu';
import DialogDelete from './DialogDelete';
import '../styles/PostBacks.scss';

const initialValuesFilter = {
    offers: [],
    types: [],
    statuses: [],
}

const mapValuesFilter = {
    offers: 'offers',
    types: 'types',
    statuses: 'statuses',
}

function isFiltered(urlSearchParams) {
    const result = Object.entries(mapValuesFilter).some((element) => urlSearchParams.get(element[1]));
    return result;
}

let abortController;

const PostBacks = (props) => {
    const { username } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [valuesFilter, setValuesFilter] = useState(initialValuesFilter);
    const [selectedPostBack, setSelectedPostBack] = useState(null);
    const [openDialogAdd, setOpenDialogAdd] = useState(false);
    const [openDialogEdit, setOpenDialogEdit] = useState(false);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [positionContextMenu, setPositionContextMenu] = useState({left: null, top: null});

    const breadcrumbs = [
        {name: 'Пользователи', to: '/users/'},
        {name: username, to: `/users/${username}/`},
        {name: 'PostBack\'и'},
    ];

    const fetch = () => {
        abortController = new AbortController();
        setLoading(true);
        props.fetch.get(`/api/postbacks/?${props.router.urlSearchParams.toString()}`, abortController)
            .then(({ok, data}) => {
                if (ok) {
                    setData(data);
                }
                setLoading(false);
            })
            .catch((error) => {
                setData(null);
                if (error.name !== 'AbortError') {
                    setLoading(false);
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    const closeContextMenu = () => {
        setPositionContextMenu({left: null, top: null});
    };

    useEffect(() => {
        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, []);

    useEffect(() => {
        let page = parseInt(props.router.urlSearchParams.get('page'));

        let _valuesFilter = {...initialValuesFilter};
        _valuesFilter.offers = props.router.getUrlSearchParam('offers').split('||');
        _valuesFilter.types = props.router.getUrlSearchParam('types').split('||');
        _valuesFilter.statuses = props.router.getUrlSearchParam('statuses').split('||');
        setValuesFilter(_valuesFilter);

        if (isNaN(page)) {
            page = 1;
        }
        props.router.urlSearchParams.set('username', username);
        // noinspection JSCheckFunctionSignatures
        props.router.urlSearchParams.set('page', page);

        fetch();
    }, [props.history.location.search]);

    const handleClickResetFilter = () => {
        props.history.push({search: '?page=1'});
    };

    const handleSubmitFilter = (event) => {
        event.preventDefault();
        props.router.urlSearchParams.set('offers', valuesFilter.offers.join('||'));
        props.router.urlSearchParams.set('types', valuesFilter.types.join('||'));
        props.router.urlSearchParams.set('statuses', valuesFilter.statuses.join('||'));
        props.router.urlSearchParams.set('page', '1');
        props.history.push({search: props.router.urlSearchParams.toString()});
    };

    /******************************************************************************************************************/

    const handleContextMenu = (pb) => (event) => {
        event.preventDefault();
        setSelectedPostBack(pb);
        setPositionContextMenu({left: event.clientX, top: event.clientY});
    };

    const handleClickEdit = () => {
        closeContextMenu();
        setOpenDialogEdit(true);
    };

    const handleClickDelete = () => {
        closeContextMenu();
        setOpenDialogDelete(true);
    };

    const handleCloseContextMenu = () => {
        closeContextMenu();
        setSelectedPostBack(null);
    };

    /******************************************************************************************************************/

    const handleAddOk = () => {
        setOpenDialogAdd(false);
        props.router.reload();
        enqueueSnackbar('PostBack добавлен', {variant: 'success'});
    };

    const handleEditOk = () => {
        setOpenDialogEdit(false);
        setSelectedPostBack(null);
        props.router.reload();
        enqueueSnackbar('PostBack изменен', {variant: 'success'});
    };

    const handleDeleteOk = () => {
        setOpenDialogDelete(false);
        setSelectedPostBack(null);
        props.router.reload();
        enqueueSnackbar('PostBack удален', {variant: 'success'});
    };

    /******************************************************************************************************************/

    // noinspection JSUnresolvedVariable
    return (
        <PostBacksContext.Provider value={{
            data: data,
            valuesFilter: valuesFilter, setValuesFilter: setValuesFilter,
            isFiltered: isFiltered(props.router.urlSearchParams),
        }}>
            <Content id="postbacks">
                <ContentHeader breadcrumbs={breadcrumbs}/>
                <ContentBody><Paper>
                    {(!loading)
                        ? <>
                            {(data && data.results)
                                ? <>
                                    <Top onClickAdd={() => setOpenDialogAdd(true)}
                                         onClickResetFilter={handleClickResetFilter}
                                         onSubmitFilter={handleSubmitFilter}
                                    />
                                    <DialogAdd open={openDialogAdd} setOpen={setOpenDialogAdd} onOk={handleAddOk}/>
                                    {(data.results.length > 0)
                                        ? <>
                                            <Table selectedPostBack={selectedPostBack} postbacks={data.results}
                                                   onContextMenu={handleContextMenu}
                                            />
                                            <ContextMenu position={positionContextMenu}
                                                         onClickEdit={handleClickEdit}
                                                         onClickDelete={handleClickDelete}
                                                         onClose={handleCloseContextMenu}
                                            />
                                            <DialogEdit selectedPostBack={selectedPostBack}
                                                        setSelectedPostBack={setSelectedPostBack}
                                                        open={openDialogEdit} setOpen={setOpenDialogEdit}
                                                        onOk={handleEditOk}
                                            />
                                            <DialogDelete selectedPostBack={selectedPostBack}
                                                          setSelectedPostBack={setSelectedPostBack}
                                                          open={openDialogDelete} setOpen={setOpenDialogDelete}
                                                          onOk={handleDeleteOk}
                                            />
                                            <Pagination totalPages={data.total_pages} currentPage={data.current_page}/>
                                        </>
                                        : null
                                    }
                                </>
                                : <ErrorFetch onClick={() => fetch()}/>
                            }
                        </>
                        : <Spinner/>
                    }
                </Paper></ContentBody>
            </Content>
        </PostBacksContext.Provider>
    );
};

let Component = PostBacks;
Component = withRouter(Component);
Component = withFetch(Component);

export default Component;
